import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { MagnifyingGlass } from '@phosphor-icons/react/dist/ssr/MagnifyingGlass';
import { useNavigation } from '../../contexts/NavigationContext';
import { useSession } from '../../contexts/SessionContext';
import { useConnectionContext } from '../../contexts/ConnectionStateContext';
import { getConnections, deleteConnection } from '../../services/storage/connectionStorage';
import { readCache, writeCache } from '../../services/storage/readCache';
import {
  loadMessageHistory,
  loadDataMessages,
  loadLatestMessages,
} from '../../services/chat/messageStore';
import {
  saveConnectionMetadata,
  saveReceivedAnonData,
  reconcileDeletedMarks,
  applyDeletedMarkChange,
  getLeftConnectionIds,
  latchSelfRooms,
  getNicknamesForConnections,
  upsertConnectionView,
  CONNECTION_METADATA_PREFIX,
  getProfileData,
} from '../../services/storage/userDataStorage';
import { isSimpleTier as isSimpleTierNow } from '../../utils/userTier';
import { isSansChatVariant, isTextVariant } from '../../utils/exchangeVariant';
import { resolveConnectionNote } from '../../utils/connectionNote';
import HostTextVenueConfirm from './variants/text/HostTextVenueConfirm';
import SendMessagePrompt from './variants/text/SendMessagePrompt';
import SendMessageOverlay from './variants/text/SendMessageOverlay';
import CardCameraButton from './variants/text/CardCameraButton';
import {
  buildSmsComposeUrl,
  buildWhatsAppComposeUrl,
  pickDmChannel,
} from '../../services/sharing/followUpText';
import { ALL_CONTACT_TYPES, openContactLink } from '../../utils/contactTypeConfig';
import { firstNameOf } from '../../utils/contactUtils';
import { ButtonRounded } from '../UISystem/Buttons';
import PeopleQrPitch from './variants/thread/PeopleQrPitch';
import { hasSimpleHostedExchange } from '../../utils/spMilestones';
import { Lightning } from '@phosphor-icons/react/dist/ssr/Lightning';
import { useConnectionData } from '../../contexts/ConnectionStateContext';
import { WaitingCard, JustFormedCard } from '../UI/Cards/HostFlowCards';
import { confirmVenue, changeVenue } from '../../services/sharing/venueConfirmation';
import { uploadChatImage } from '../../services/storage/chatImageUpload';
import { buildImageChatMessage } from '../../services/chat/messageBuilders';
import { sendMessage } from '../../services/chat/chatService';
import { persistMessage } from '../../services/chat/messageStore';
import LocationOverlay from '../Overlay/LocationOverlay';
import {
  getOrphanedConnections,
  confirmHostConnection,
  getConnectionProfiles,
} from '../../services/activeSession';
import {
  formatLocationForCard,
  formatLocationPillPrimary,
  formatMetSentence,
  gateVenueMetadata,
} from '../../utils/locationFormatters';
import { formatRelativeTime, currentUtcOffsetMinutes } from '../../utils/dateHelpers';
import { mintMeetingKey } from '../../services/meetings/meetingRule';
import { getSenderId } from '../../utils/identityUtils';
import { sendPhotoMoment } from '../../services/moments/momentStore';
import { isNewExperience } from '../../utils/newExperience';
import { isRoyaleUser } from '../../utils/contactType';
import {
  getConnectionDisplayName,
  enrichPeerAnonData,
  getSharedHometown,
} from '../../utils/connectionName';
import { getSharedItems } from '../../services/sharing/connectionSharing';
import { hasData } from '../../utils/objectHelpers';
import {
  hasUnreadMessages,
  getTime,
  formatMessagePreview,
  latestSharedImageUrl,
} from '../../utils/chatHelpers';
import { nanoid } from 'nanoid';
import SwipeableConnectionCard from '../UI/Cards/SwipeableConnectionCard';
import ProfileConnectionsHeader from '../UISystem/Headers/ProfileConnectionsHeader';
import { COLORS } from '../../styles/UISystem';
import { getLocationWithVenue } from '../../services/location';
import QuickAddOverlay from '../Overlay/QuickAddOverlay';
import ShareLocationSheet from '../Overlay/ShareLocationSheet';
import NotificationPrompt from '../Notifications/NotificationPrompt';
import {
  isPushSupported,
  getPermissionState,
  hasSeenNotificationPrompt,
  markNotificationPromptSeen,
} from '../../services/pushNotificationService';
import useGroups from '../../hooks/useGroups';

import {
  logManualAdd,
  logConnectionLocation,
  logAppOpen,
  logEvent,
  logMomentEntryTapped,
  logMomentSent,
  logConnectionDeleted,
  logConnectionError,
  updateLastActive,
} from '../../services/analytics/eventLogger';
import useLocationPromptComplete from '../../hooks/useLocationPromptComplete';
import {
  getLocationSharingEnabled,
  setLocationSharing,
  getConnectionLocations,
  formatNeighborhoodDisplay,
  updateLiveLocation,
  hydrateSharingFromDb,
} from '../../services/location/liveLocationService';
import { hasLocationPermission } from '../../services/location';
import LocationPrompt from '../Location/LocationPrompt';
import BrowserPermissionModal from '../Location/BrowserPermissionModal';
import LocationDebugPanel from '../Debug/LocationDebugPanel'; // dev-only diagnostic
import DevToolsPills from '../Debug/DevToolsPills'; // dev-only header pills
import { isDevToolsEnabled, isBranchDeploy } from '../../utils/devTools';
import { useNearbyGroups } from '../../hooks/useNearbyGroups';
import NearbyList from './NearbyList';
import { NEARBY_TEST_DATA, NEARBY_TEST_LIVE_RECORDS } from '../../services/nearby/nearbyTestData';

/**
 * ProfileConnectionsListPage Component
 *
 * Displays a list of ALL connections for profile users
 * Shows all anonymous users who have connected with them
 */
const ProfileConnectionsListPage = () => {
  const { navigateTo, showQR, isQROpen } = useNavigation();

  // Get navigation params
  const { screenParams } = useNavigation();

  // Get session context
  const { updateSessionChatRoomId } = useSession();

  // Get connection context for manual add persistence
  const {
    updateManualContactData,
    updateNotes,
    updateConnectionMetadata,
    updateAnonData,
    loadConnectionData,
  } = useConnectionContext();

  // Groups hook (DB-first)
  const { getGroupsForConnection, getDisplayGroups, loading: groupsLoading } = useGroups();

  // State for connection data - now an array of all connections
  const [connections, setConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null); // Selected group filter
  const [availableGroups, setAvailableGroups] = useState([]); // Groups with counts: [{key, label, count}]

  // Location sharing state
  const [isLocationSharing, setIsLocationSharing] = useState(() => getLocationSharingEnabled());
  // The owner's stored location from the DB hydrate — seeds the share sheet so a
  // lit pin opens with the neighbourhood already shown (no blank flash).
  const [ownLocation, setOwnLocation] = useState(null);
  // chatRoomId → "Neighborhood, City" — painted from the shared cache so the
  // green pills render with the list (stale-while-revalidate; only when sharing)
  const [connectionLocations, setConnectionLocations] = useState(() => {
    if (!getLocationSharingEnabled()) return {};
    const cached = readCache('connection_locations_cache');
    return cached.hit && cached.value ? cached.value : {};
  });
  const [showShareSheet, setShowShareSheet] = useState(false);
  // Notification permission pre-prompt — first People-list visit (see effect below)
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [shareError, setShareError] = useState(null); // inline error in the share sheet
  const [shareSubmitting, setShareSubmitting] = useState(false); // enable in flight

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Simple-tier chrome (tiered-exchange Step 6): SP avatar header, Connect
  // pill + Get-app banner, no search/chips, no notification prompt (push is
  // Full-only). Tier is asked, never re-derived.
  const isSimpleTier = isSimpleTierNow();
  const [spProfile, setSpProfile] = useState(null);
  // NO Get-Royale door at Simple tier (user ruling 2026-08-16, matching the
  // Anon People page): the milestone measures whether people make and USE a
  // QR, and a second progression under the first splits exactly the attention
  // that test needs — doubly so here, where they have just made one. The
  // signup door still exists at Full tier, on the expired screen and via
  // Profile-sent invites.
  // Re-read on every QR open/close, not just on mount. The photo is chosen on
  // the QR screen, which is an OVERLAY over this page — this page never
  // unmounts, so a mount-only read left the header avatar empty until you
  // navigated away and back (user, device 2026-08-16: "you have to click into
  // it first"). isQROpen is the existing signal for that beat; no new event.
  useEffect(() => {
    if (!isSimpleTier) return;
    getProfileData().then((p) => setSpProfile(p || null));
  }, [isSimpleTier, isQROpen]);

  // ── Host card flow (Step 7; mocks profile_2–6) — ANY profile-world host
  // (Full or SP), not tier-gated. The waiting card shows while a QR session
  // sits open with no scan yet; the just-formed card carries the inline
  // venue-confirm and Take-photo prompt rows, then settles into the normal
  // card. Stage per room rides sessionStorage (survives remounts, dies with
  // the session).
  const hostFlowRoom = isSansChatVariant() ? screenParams?.justFormed || null : null;
  // TEXT choreography (plan 2, epic §9): same stage machine, three deltas —
  // the venue stage renders as a FULL PAGE (not inline rows), the waiting
  // card never shows (lurker cards accepted: the placeholder card is
  // immediate), and the name gates drop. CARD behavior untouched.
  const textVariant = isTextVariant();
  // Live subscription: the peer's share-back lands via the global handler →
  // context update → the card's name/venue fill in without a reload.
  const hostFlowData = useConnectionData(hostFlowRoom);
  const [hostCardStage, setHostCardStage] = useState(null);
  useEffect(() => {
    if (!hostFlowRoom) {
      setHostCardStage(null);
      return;
    }
    const stored = sessionStorage.getItem(`host_card_stage_${hostFlowRoom}`);
    // 'done' = flow completed — settled card, never re-prompt. It must be a
    // stored sentinel, not a removed key: an ABSENT key is what a fresh
    // formation looks like, so removal made every People remount re-open the
    // venue stage (device pass 2: "asking about the venue again").
    setHostCardStage(stored === 'done' ? null : stored || 'venue');
  }, [hostFlowRoom]);
  const advanceHostStage = (next) => {
    if (!hostFlowRoom) return;
    if (next) {
      sessionStorage.setItem(`host_card_stage_${hostFlowRoom}`, next);
    } else {
      sessionStorage.setItem(`host_card_stage_${hostFlowRoom}`, 'done');
      // Settle = the flow card leaves — the normal card must already be in
      // list state or the connection vanishes ("poof") until the next reload
      // (device pass 2: photo-skip dropped the card; only photo-post refreshed)
      loadConnectionsFromStorage('host-flow-settled').then(enrichConnections);
    }
    setHostCardStage(next);
  };
  const [showHostVenueOverlay, setShowHostVenueOverlay] = useState(false);

  const hostMetadata = hostFlowData?.connectionMetadata || null;

  // ── People owns the CATCH-UP layer for fresh rooms (device pass 1 +
  // 1g, 2026-08-13). The two-layer receive system (message-types-and-
  // delivery.md) promises realtime via the channel PLUS loadConnectionData
  // catch-up — People had only the QR screen's kept-alive channel, which iOS
  // kills on lock. First fix keyed the poll on the justFormed params — the
  // THIRD victim of the nav-source rot class (name stayed missing after a
  // camera trip until a detail round-trip did its own catch-up read). Now it
  // keys on the ARMED sentinels like the prompt and camera: any armed, fresh
  // (<24h), still-incomplete room in the list gets the 3s read; the tick
  // then refreshes list state from storage (localStorage only — enrichment
  // stays on its own triggers). Capped per mount; read-only, litmus-safe.
  // "Has the Anon's contact landed?" — ONE predicate over every channel the
  // share sheet offers (/simplify: the hand-rolled phone/email/instagram
  // triple missed WhatsApp/LinkedIn shares and polled those rooms forever)
  const hasAnonContact = (anonData) => Boolean(anonData?.name && pickDmChannel(anonData));
  // Computed only under TEXT — no filter/storage reads on CHAT/CARD renders
  const computeCatchUpRooms = () => {
    const rooms = connections
      .filter((c) => {
        if (c.isManualContact) return false;
        if (localStorage.getItem(`send_prompt_armed_${c.chatRoomId}`) !== 'true') return false;
        if (hasAnonContact(c.anonData)) return false;
        const met = c.connectionMetadata?.meetingDate || c.connectionMetadata?.timestamp;
        return Boolean(met && Date.now() - new Date(met).getTime() < 24 * 60 * 60 * 1000);
      })
      .map((c) => c.chatRoomId);
    // The just-formed room may not be in list state yet on the first beats
    if (hostFlowRoom && !hasAnonContact(hostFlowData?.anonData)) rooms.push(hostFlowRoom);
    return [...new Set(rooms)];
  };
  const catchUpKey = textVariant ? computeCatchUpRooms().join(',') : '';
  // Cap survives room-set changes (/simplify: a per-effect counter granted
  // the poll a fresh 3-minute window every time the room set shifted)
  const catchUpTriesRef = useRef(0);
  useEffect(() => {
    if (!catchUpKey) return undefined;
    const rooms = catchUpKey.split(',');
    const timer = setInterval(async () => {
      catchUpTriesRef.current += 1;
      if (catchUpTriesRef.current > 60) {
        clearInterval(timer);
        return;
      }
      await Promise.all(rooms.map((room) => loadConnectionData(room)));
      // Storage-only refresh so recovered names paint on the list cards
      loadConnectionsFromStorage('text-catch-up');
    }, 3000);
    return () => clearInterval(timer);
    // loadConnectionsFromStorage is a stable page-scope function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catchUpKey, loadConnectionData]);

  const handleHostVenueConfirm = async () => {
    try {
      await confirmVenue(hostFlowRoom, hostMetadata, { updateConnectionMetadata });
    } catch (err) {
      console.error('[HOST_FLOW] Venue confirm failed:', err);
    }
    advanceHostStage('photo');
  };
  const handleHostVenuePick = async (selectedVenue) => {
    // Custom-venue persistence lives INSIDE changeVenue (the one home —
    // venueConfirmation.js saves picked customs for 100m recall). B30's
    // review re-diagnosis: the save was never missing here.
    try {
      await changeVenue(hostFlowRoom, hostMetadata, selectedVenue, { updateConnectionMetadata });
    } catch (err) {
      console.error('[HOST_FLOW] Venue pick failed:', err);
    }
    setShowHostVenueOverlay(false);
    advanceHostStage('photo');
  };
  // Parameterized by room (device pass 1b): the TEXT camera also rides the
  // NORMAL list card via the armed sentinel below, so the spine can't assume
  // the flow room. Success writes the photo done sentinel (camera retires —
  // the thumbnail is the receipt).
  const handleCardPhoto = async (room, file) => {
    try {
      const publicUrl = await uploadChatImage(file, room);
      if (!publicUrl) throw new Error('upload returned no URL');
      const message = buildImageChatMessage({
        senderId: getSenderId(),
        senderType: 'profile',
        imageUrl: publicUrl,
      });
      // await parity with the chat pages' copies (review 2026-08-09 —
      // born-drift note; full spine lift logged in Future Work)
      await sendMessage(room, message);
      await persistMessage(room, message);
      // D3 Moments: the host-flow exchange photo also becomes the meeting's
      // first photo-moment (additive). Only the host-flow room — the ad-hoc
      // list camera targets an arbitrary (possibly out-of-window) connection
      // with no night-key inputs in hand.
      if (isNewExperience() && room === hostFlowRoom && hostMetadata) {
        const { ok } = await sendPhotoMoment(room, hostMetadata, publicUrl);
        if (ok) logMomentSent(room, 'host_flow');
      }
      localStorage.setItem(`photo_prompt_done_${room}`, 'true');
      setPromptRetireTick((t) => t + 1);
      if (room === hostFlowRoom) {
        advanceHostStage(null); // settle refreshes the list — thumbnail is the receipt
      } else {
        loadConnectionsFromStorage('card-photo-posted').then(enrichConnections);
      }
    } catch (err) {
      // Never dismiss on failure (device pass 1): the button stays for retry,
      // and the miss is observable instead of silently swallowed.
      console.error('[HOST_FLOW] Photo share failed — keeping the prompt:', err);
      logConnectionError('host_photo_share_failed', {
        chatRoomId: room,
        message: err?.message || 'photo share failed',
      });
    }
  };

  // ── TEXT one-shot send prompt (plan 2, epic §4/§9). ARMING is a per-room
  // localStorage sentinel written when the formation lands — deliberately NOT
  // dependent on justFormed params surviving (the nav-source-gated-trigger
  // rot class): a number that arrives after the host wandered off still
  // surfaces the prompt on any later People visit, exactly once. X or send
  // writes the done sentinel — retired for good, across sessions (localStorage,
  // not sessionStorage: an installed-PWA relaunch is a new session).
  const [sendOverlayRoom, setSendOverlayRoom] = useState(null);
  const [, setPromptRetireTick] = useState(0); // store is localStorage — tick re-renders after retire
  useEffect(() => {
    if (textVariant && hostFlowRoom) {
      localStorage.setItem(`send_prompt_armed_${hostFlowRoom}`, 'true');
      // Camera arms the same way (device pass 1b: it lived only on the
      // params-dependent flow card and vanished on reload — the same rot
      // class the send prompt was armed against)
      localStorage.setItem(`photo_prompt_armed_${hostFlowRoom}`, 'true');
    }
  }, [textVariant, hostFlowRoom]);
  const retireSendPrompt = (room) => {
    localStorage.setItem(`send_prompt_done_${room}`, 'true');
    setPromptRetireTick((t) => t + 1);
  };
  // Needs a DM-able channel — phone wins, else the shared social/email
  // (device pass 1j: copy + open); never behind the full-page venue confirm
  const shouldShowSendPrompt = (room, anonData) =>
    textVariant &&
    Boolean(pickDmChannel(anonData)) &&
    localStorage.getItem(`send_prompt_armed_${room}`) === 'true' &&
    !localStorage.getItem(`send_prompt_done_${room}`) &&
    !(room === hostFlowRoom && hostCardStage === 'venue');
  // Card camera (device pass 1b): armed at formation, retires on photo post;
  // 24h freshness bound so old cards don't carry cameras forever (quiet
  // auto-retire — presentational, nothing written).
  const CARD_CAMERA_FRESH_MS = 24 * 60 * 60 * 1000;
  const shouldShowCardCamera = (room, metadata) => {
    if (!textVariant) return false;
    if (localStorage.getItem(`photo_prompt_armed_${room}`) !== 'true') return false;
    if (localStorage.getItem(`photo_prompt_done_${room}`)) return false;
    const met = metadata?.meetingDate || metadata?.timestamp;
    return Boolean(met && Date.now() - new Date(met).getTime() < CARD_CAMERA_FRESH_MS);
  };
  const sendOverlayData = sendOverlayRoom
    ? sendOverlayRoom === hostFlowRoom
      ? {
          anonData: hostFlowData?.anonData,
          connectionMetadata: hostFlowData?.connectionMetadata,
        }
      : connections.find((c) => c.chatRoomId === sendOverlayRoom) || null
    : null;
  const sendOverlayChannel = sendOverlayData ? pickDmChannel(sendOverlayData.anonData) : null;
  const handleSendOverlaySend = ({ body, linkIncluded }) => {
    const room = sendOverlayRoom;
    const channel = sendOverlayChannel;
    logEvent('send_message_sent', { chatRoomId: room, linkIncluded, channel: channel?.type });
    retireSendPrompt(room);
    setSendOverlayRoom(null);
    if (channel?.type === 'phone') {
      // Opens the host's OWN Messages composer, prefilled + addressed (V1)
      window.location.href = buildSmsComposeUrl(channel.value, body);
      return;
    }
    if (channel?.type === 'whatsapp') {
      // wa.me carries the text natively — one-tap direct send, like SMS
      openContactLink(buildWhatsAppComposeUrl(channel.value, body));
      return;
    }
    // Other non-phone (device pass 1j): the overlay already copied the
    // text — open the shared channel so the host pastes into the DM
    const link = channel && ALL_CONTACT_TYPES[channel.type]?.getLink?.(channel.value);
    if (link) openContactLink(link);
  };

  // 7b REVISED (device pass 1, user ruling 2026-08-08): the SP location ask
  // fires on the CONNECT tap — never on People land (it stacked over the QR
  // customize sheet, and the effect double-fired). Soft prompt once, then the
  // QR opens whatever the answer was; later Connect taps go straight to QR.
  const spLocationPrompt = useLocationPromptComplete({
    context: 'connecting',
    onGranted: () => {},
  });
  const handleSpConnectPress = async () => {
    if (localStorage.getItem('sp_location_prompted') !== 'true') {
      // Sync guard BEFORE any await — the ask happens exactly once, ever
      localStorage.setItem('sp_location_prompted', 'true');
      const granted = await hasLocationPermission();
      if (!granted) {
        spLocationPrompt.setShowLocationPrompt(true);
        return; // QR opens when the prompt completes (see onComplete wrapper)
      }
    }
    showQR();
  };

  // Notification pre-prompt — the one deliberate ask: first People-list visit
  // while the permission is still undecided. The seen flag is set on any
  // response (granted/denied/dismissed) so the user is never re-nagged.
  // Full-only: push is a Full feature — an SP is never asked.
  useEffect(() => {
    if (isLoading || isSimpleTier) return;
    if (hasSeenNotificationPrompt()) return;
    if (!isPushSupported() || getPermissionState() !== 'default') return;
    setShowNotificationPrompt(true);
  }, [isLoading, isSimpleTier]);

  // Nearby tab — hardcoded mode alongside the group-derived chips
  const [nearbyActive, setNearbyActive] = useState(false);

  // DEV-only Nearby test data (null | 'light' | 'full' | 'live') — merged
  // in-memory into the grouping input, never written to the DB. Gated to
  // non-prod at render.
  const [testMode, setTestMode] = useState(() => localStorage.getItem('nearby_test_mode') || null);
  const toggleTestData = (mode) => {
    const next = testMode === mode ? null : mode;
    setTestMode(next);
    if (next) {
      localStorage.setItem('nearby_test_mode', next);
      setNearbyActive(true); // jump to Nearby so the change is visible
    } else {
      localStorage.removeItem('nearby_test_mode');
    }
  };
  const nearbyInput = useMemo(
    () => (testMode ? [...connections, ...NEARBY_TEST_DATA[testMode]] : connections),
    [connections, testMode]
  );
  // 'live' mode also injects synthetic live-location rows (resolved by coords)
  const testLiveRecords = testMode === 'live' ? NEARBY_TEST_LIVE_RECORDS : null;
  // Card location display: merge the test live neighborhoods (green pill) in 'live' mode
  const displayLocations = useMemo(() => {
    if (testMode !== 'live') return connectionLocations;
    const merged = { ...connectionLocations };
    for (const [id, r] of Object.entries(NEARBY_TEST_LIVE_RECORDS)) {
      merged[id] = formatNeighborhoodDisplay(r.neighborhood, r.city);
    }
    return merged;
  }, [connectionLocations, testMode]);
  const {
    groups: nearbyGroups,
    hasAnchor: nearbyHasAnchor,
    loading: nearbyLoading,
  } = useNearbyGroups(nearbyInput, {
    isLocationSharing,
    enabled: nearbyActive,
    testLiveRecords,
  });

  // Scroll-direction tracking for search/chips visibility
  const [showSearchBar, setShowSearchBar] = useState(true);
  // Dev chrome. Read ONCE at mount, not per render: the flag only changes via a
  // ?dev= URL, which reloads the page anyway, and a per-render localStorage
  // read in a list page is a hot-path cost for a value that cannot move.
  const [devToolsOn] = useState(isDevToolsEnabled);
  const [showLocPanel, setShowLocPanel] = useState(false);
  const lastScrollY = useRef(0);
  const scrollContainerRef = useRef(null);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const currentY = container.scrollTop;
    // Show on scroll up or near top, hide on scroll down
    if (currentY <= 10) {
      setShowSearchBar(true);
    } else if (currentY < lastScrollY.current) {
      setShowSearchBar(true);
    } else if (currentY > lastScrollY.current + 5) {
      setShowSearchBar(false);
    }
    lastScrollY.current = currentY;
  }, []);

  // Quick-add overlay state
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddChatRoomId, setQuickAddChatRoomId] = useState(null);
  const [quickAddMetadata, setQuickAddMetadata] = useState(null);

  // Format location string - use card-specific formatter
  const formatLocation = formatLocationForCard;

  // Filter AND sort connections — derived computation, always fresh.
  const filteredConnections = useMemo(() => {
    let result = [...connections];

    // Filter by group if selected
    if (selectedCategory) {
      result = result.filter((c) => {
        const connGroups = getGroupsForConnection(c.chatRoomId);
        // "Friends" filter includes Close Friends (subset rule)
        if (selectedCategory === 'friends') {
          return connGroups.some((g) => g.name === 'Friends' || g.name === 'Close Friends');
        }
        return connGroups.some((g) => g.name.toLowerCase() === selectedCategory);
      });
    }

    // Search filter — match name, nickname, note, or location/venue (the note
    // no longer renders on the card but the data still exists on detail)
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((c) => {
        const name = (getConnectionDisplayName(c) || '').toLowerCase();
        // One resolved note, not two raw columns. `c.notes?.text` was dead —
        // notes is a string in this shape (connectionStorage builds it from
        // profileNotes || anonNotes), so `.text` was always undefined and a
        // legacy note has never been searchable.
        const nickname = resolveConnectionNote(c).text.toLowerCase();
        const note = '';
        const loc = formatLocation(c.connectionMetadata).toLowerCase();
        const city = (getSharedHometown(c.anonData) || '').toLowerCase();
        return (
          name.includes(q) ||
          nickname.includes(q) ||
          note.includes(q) ||
          loc.includes(q) ||
          city.includes(q)
        );
      });
    }

    // Sort by met timestamp — NaN-safe via getTime (0 fallback instead of Invalid Date)
    result.sort(
      (a, b) =>
        getTime(
          b.connectionMetadata?.timestamp ||
            b.connectionMetadata?.meetingDate ||
            b.connectionMetadata?.lastUpdated
        ) -
        getTime(
          a.connectionMetadata?.timestamp ||
            a.connectionMetadata?.meetingDate ||
            a.connectionMetadata?.lastUpdated
        )
    );

    return result;
  }, [connections, selectedCategory, searchQuery, getGroupsForConnection, formatLocation]);

  // Reset scroll position and filter state on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    // Reset filters to default state
    setSelectedCategory(null);
    // Track app open (30-min dedup built into logAppOpen)
    logAppOpen();
    // Update last_active_at for activity status display (5-min dedup)
    updateLastActive();
  }, []);

  // Derive unread chat indicator from enriched connections data.
  // Runs after enrichConnections adds latestMessage to each connection.
  useEffect(() => {
    const chatsLastViewed = localStorage.getItem('chats_sort_last_viewed');
    const chatsLastViewedDate = chatsLastViewed ? new Date(chatsLastViewed) : null;
    const anyUnread = connections.some((conn) => {
      if (conn.isManualContact || !conn.latestMessage?.timestamp) return false;
      if (hasUnreadMessages([conn.latestMessage], conn.chatRoomId) === false) return false;
      if (chatsLastViewedDate && new Date(conn.latestMessage.timestamp) <= chatsLastViewedDate)
        return false;
      return true;
    });
    if (anyUnread) {
      localStorage.setItem('has_unread_chats', 'true');
    }
  }, [connections]);

  // Refresh connections when app returns to foreground
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[PROFILE_CONNECTIONS_LIST] App became visible, refreshing connections');
        const validConnections = await loadConnectionsFromStorage('visibilitychange');
        enrichConnections(validConnections);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate the sharing flag from the DB on mount. The localStorage flag (read
  // for instant paint above) can drift false while the DB says the user is
  // sharing — a fresh origin, cleared storage, or the pre-Oct-6 writer that no
  // longer sets it. The DB is the source of truth for the pin's state. See
  // location-share-sync.
  useEffect(() => {
    const profileUserId = localStorage.getItem('profile_user_id');
    if (!profileUserId) return;
    hydrateSharingFromDb(profileUserId).then(({ enabled, location, error }) => {
      // No-Fallbacks exception (discussed): on a read error we kept the last
      // value and don't force the prompt — but log it so it's queryable, never
      // a silent swallow.
      if (error) {
        logConnectionError('location_sharing_read_failed', {
          stage: 'people_hydrate',
          profileUserId,
          error,
        });
      }
      setIsLocationSharing(enabled);
      setOwnLocation(location);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch live locations for connections (only if sharing is on — reciprocity)
  // Re-fetches on mount, toggle change, connections change, and return from background
  useEffect(() => {
    if (!isLocationSharing) {
      setConnectionLocations({});
      return;
    }
    const profileUserId = localStorage.getItem('profile_user_id');
    if (!profileUserId) return;

    const fetchLocations = () => {
      getConnectionLocations(profileUserId)
        .then((locations) => {
          writeCache('connection_locations_cache', locations);
          setConnectionLocations(locations);
        })
        .catch((err) => {
          // Keep the cached pills on screen — a failed refresh must not blank them
          console.error('[PROFILE_CONNECTIONS_LIST] Locations fetch failed:', err?.message);
        });
    };

    fetchLocations();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchLocations();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isLocationSharing, connections]);

  // Tap the pin → open the share sheet (both directions). When turning ON
  // without permission, gate on the "Access location" pre-prompt first; on
  // grant it opens the sheet to confirm.
  const handleLocationToggle = async () => {
    setShareError(null); // clear any error from a prior attempt
    if (!isLocationSharing) {
      const granted = await hasLocationPermission();
      if (!granted) {
        setShowLocationPrompt(true);
        return;
      }
    }
    setShowShareSheet(true);
  };

  // GPS update + enable. Don't flip isLocationSharing until GPS succeeds —
  // prevents a brief location flash on failure. Returns {ok, reason} so the
  // caller can surface the failure instead of closing the sheet silently.
  const enableLocationSharing = async (profileUserId) => {
    const result = await updateLiveLocation(profileUserId);
    if (!result.ok) return { ok: false, reason: result.error || 'unknown' };

    setIsLocationSharing(true);
    await setLocationSharing(profileUserId, true);
    return { ok: true };
  };

  // Sheet confirm — actually start or stop sharing + analytics. On enable
  // failure: never close silently — route code-1 denials to the Settings-help
  // modal, keep the sheet open with an inline retry on GPS timeout, and log
  // every failure so it's queryable in /admin/errors.
  const handleShareSheetConfirm = async () => {
    const profileUserId = localStorage.getItem('profile_user_id');
    if (!profileUserId) {
      setShowShareSheet(false);
      return;
    }

    // Turning OFF — local, always succeeds.
    if (isLocationSharing) {
      setIsLocationSharing(false);
      await setLocationSharing(profileUserId, false);
      logEvent('location_sharing_disabled');
      setShowShareSheet(false);
      return;
    }

    // Turning ON.
    setShareSubmitting(true);
    setShareError(null);
    const { ok, reason } = await enableLocationSharing(profileUserId);
    setShareSubmitting(false);

    if (ok) {
      logEvent('location_sharing_enabled');
      setShowShareSheet(false);
      return;
    }

    // Failed — make it visible and queryable.
    logConnectionError('location_share_failed', { reason, stage: 'share_sheet', profileUserId });
    if (reason === 'permission_denied') {
      // Browser hard-denied — Settings guidance (mirrors the scan flow).
      setShowShareSheet(false);
      setShowBrowserPermissionModal(true);
    } else {
      // GPS timeout / no neighborhood — keep the sheet open to retry.
      setShareError("Couldn't get your location — try again.");
    }
  };

  // Permission pre-prompt completion + terminal permission modal — the shared
  // grant/deny/dismiss branching lives in useLocationPromptComplete (sharing
  // context: failures log location_share_failed so they're never invisible);
  // only the share-flow proceed (open the sheet) is local. The share-sheet
  // handler above also opens the modal via setShowBrowserPermissionModal.
  const {
    showLocationPrompt,
    setShowLocationPrompt,
    showPermissionModal: showBrowserPermissionModal,
    setShowPermissionModal: setShowBrowserPermissionModal,
    handlePromptComplete: handleLocationPromptComplete,
    dismissPermissionModal: handleDismissPermissionModal,
  } = useLocationPromptComplete({
    context: 'sharing',
    onGranted: () => setShowShareSheet(true),
  });

  // Load connections from localStorage (async but instant — no network calls).
  // Returns validConnections array for enrichment.
  const loadConnectionsFromStorage = async (caller = 'unknown') => {
    console.log('[PROFILE_CONNECTIONS_LIST] Loading from storage, caller:', caller);
    const allConnections = await getConnections();

    if (!allConnections || !allConnections.length) {
      console.log('[PROFILE_CONNECTIONS_LIST] No connections found');
      setConnections([]);
      setIsLoading(false);
      return [];
    }

    const validConnections = allConnections
      .filter((conn) => {
        // A self-room (own merged Simple profile — f/simple-merge) is never listed
        if (conn.anonData?.selfRoom) return false;
        if (hasData(conn.manualContactData)) {
          const name = conn.manualContactData.name || '';
          return name.trim().length > 0;
        }
        return true;
      })
      .map((conn) => {
        if (hasData(conn.manualContactData)) {
          return {
            chatRoomId: conn.chatRoomId,
            anonData: conn.manualContactData,
            connectionMetadata: conn.connectionMetadata || {},
            chatMessages: [],
            notes: { text: conn.notes || '' },
            hasSharedContactInfo: Boolean(
              conn.manualContactData?.phone ||
                conn.manualContactData?.email ||
                conn.manualContactData?.instagram
            ),
            isManualContact: true,
          };
        }

        const dataSource = hasData(conn.anonData) ? conn.anonData : {};
        return {
          chatRoomId: conn.chatRoomId,
          anonData: dataSource,
          displayName: conn.displayName || null,
          connectionMetadata: conn.connectionMetadata || {},
          chatMessages: conn.chatMessages || [],
          notes: { text: conn.notes || '' },
          hasSharedContactInfo: Boolean(
            dataSource?.phone || dataSource?.email || dataSource?.instagram
          ),
          isManualContact: false,
        };
      });

    // Group filter pills are built by the useEffect below once groups finish loading.
    // MERGE-PRESERVE enrichment fields (device pass 1h: the photo thumbnail
    // flashed and vanished — a storage rebuild wiped latestMessage/nickname
    // until the next full enrichment; the catch-up poll made it rhythmic, but
    // every reload caller had the same blank-until-enriched flash).
    setConnections((prev) => {
      const prevById = Object.fromEntries(prev.map((c) => [c.chatRoomId, c]));
      return validConnections.map((conn) => {
        const old = prevById[conn.chatRoomId];
        if (!old) return conn;
        return {
          ...conn,
          latestMessage: old.latestMessage || null,
          nickname: old.nickname || conn.nickname || null,
        };
      });
    });
    setIsLoading(false);

    console.log('[PROFILE_CONNECTIONS_LIST] Loaded', validConnections.length, 'connections');
    return validConnections;
  };

  // Enrich connections from Supabase — async, progressive.
  // Each query updates state independently as it resolves.
  const enrichConnections = (validConnections) => {
    if (!validConnections || validConnections.length === 0) return;
    const chatRoomIds = validConnections.filter((c) => !c.isManualContact).map((c) => c.chatRoomId);
    const profileUserId = localStorage.getItem('profile_user_id');

    // 1. Profile lookup — resolve current names/photos for converted users; plus
    // the live-sharing resolver (D2), which wholesale-replaces each peer's shared
    // items. sharedByRoom is null when unreadable (flag off / offline) → cache stands.
    if (profileUserId) {
      Promise.all([getConnectionProfiles(profileUserId), getSharedItems(profileUserId)])
        .then(([profiles, sharedByRoom]) => {
          const hasProfiles = profiles && profiles.length > 0;
          // sharedByRoom non-null only when the flag is on AND the read succeeded.
          if (!hasProfiles && sharedByRoom == null) return;
          const profileMap = {};
          for (const p of profiles || []) {
            profileMap[p.chat_room_id] = p;
          }
          setConnections((prev) =>
            prev.map((conn) => {
              if (conn.isManualContact) return conn;
              const updatedAnonData = enrichPeerAnonData(
                conn.anonData,
                profileMap[conn.chatRoomId],
                sharedByRoom,
                conn.chatRoomId
              );
              if (!updatedAnonData) return conn;
              updateAnonData(conn.chatRoomId, updatedAnonData);
              return { ...conn, anonData: updatedAnonData };
            })
          );
        })
        .catch((err) => console.error('[PROFILE_CONNECTIONS_LIST] Profile lookup failed:', err));
    }

    // 2. Latest messages — for sort order and preview text
    if (chatRoomIds.length > 0) {
      loadLatestMessages(chatRoomIds)
        .then((latestMessages) => {
          setConnections((prev) =>
            prev.map((conn) => ({
              ...conn,
              latestMessage: latestMessages[conn.chatRoomId] || conn.latestMessage || null,
            }))
          );
        })
        .catch((err) => console.error('[PROFILE_CONNECTIONS_LIST] Latest messages failed:', err));
    }

    // 3. Deleted check: the deleted mark follows the server both ways —
    // set for a confirmed-gone peer, cleared for one confirmed to exist (one read)
    reconcileDeletedMarks(validConnections)
      .then((changes) => {
        if (changes.deleted.length === 0 && changes.revived.length === 0) return;
        setConnections((prev) =>
          prev.map((conn) => {
            const anonData = applyDeletedMarkChange(conn, changes);
            return anonData ? { ...conn, anonData } : conn;
          })
        );
      })
      .catch((err) => console.error('[PROFILE_CONNECTIONS_LIST] Deleted check failed:', err));

    // 3c. Peer-left check (CD #15) — the peer deleted this connection / their
    // account's side of it; latch peerLeft so my copy freezes + becomes editable
    // (isPeerGone), exactly like a deleted-account peer. They are not told.
    getLeftConnectionIds(validConnections, profileUserId)
      .then((leftChatRoomIds) => {
        if (leftChatRoomIds.length === 0) return;
        setConnections((prev) =>
          prev.map((conn) => {
            if (!leftChatRoomIds.includes(conn.chatRoomId)) return conn;
            const updatedAnonData = { ...conn.anonData, peerLeft: true };
            saveReceivedAnonData(conn.chatRoomId, updatedAnonData);
            return { ...conn, anonData: updatedAnonData };
          })
        );
      })
      .catch((err) => console.error('[PROFILE_CONNECTIONS_LIST] Peer-left check failed:', err));

    // 3b. Self-rooms (f/simple-merge) — latched in storage, dropped here
    latchSelfRooms(validConnections, profileUserId)
      .then((selfIds) => {
        if (selfIds.length > 0)
          setConnections((prev) => prev.filter((conn) => !selfIds.includes(conn.chatRoomId)));
      })
      .catch((err) => console.error('[PROFILE_CONNECTIONS_LIST] Self-room check failed:', err));

    // 4. Nicknames — the owner's per-connection descriptors (DB-first, batched).
    // All connections including manual contacts — they have connection_views rows too.
    const allChatRoomIds = validConnections.map((c) => c.chatRoomId);
    if (profileUserId && allChatRoomIds.length > 0) {
      getNicknamesForConnections(allChatRoomIds, profileUserId)
        .then((nicknames) => {
          if (Object.keys(nicknames).length === 0) return;
          setConnections((prev) =>
            prev.map((conn) =>
              nicknames[conn.chatRoomId] ? { ...conn, nickname: nicknames[conn.chatRoomId] } : conn
            )
          );
        })
        .catch((err) => console.error('[PROFILE_CONNECTIONS_LIST] Nickname lookup failed:', err));
    }
  };

  // Load on mount and when screen params change
  useEffect(() => {
    const recoverMissedConnections = async () => {
      const profileUserId = localStorage.getItem('profile_user_id');
      if (!profileUserId) return;

      const orphans = await getOrphanedConnections(profileUserId);
      for (const orphan of orphans) {
        console.log(
          '[PROFILE_CONNECTIONS_LIST] Recovering missed connection:',
          orphan.chat_room_id
        );
        await confirmHostConnection(orphan.chat_room_id, profileUserId, orphan.exchange_type);

        const metadataKey = `${CONNECTION_METADATA_PREFIX}${orphan.chat_room_id}`;
        if (!localStorage.getItem(metadataKey)) {
          await saveConnectionMetadata(orphan.chat_room_id, {
            meetingDate: orphan.guest_confirmed_at,
            exchangeType: orphan.exchange_type,
          });
        }

        await loadMessageHistory(orphan.chat_room_id);
        const dataMessages = await loadDataMessages(orphan.chat_room_id);
        const mySenderId = getSenderId();
        const contactMessages = dataMessages.filter(
          (msg) => msg.type === 'contact' && msg.anonData?.name && msg.senderId !== mySenderId
        );
        if (contactMessages.length > 0) {
          const latest = contactMessages[contactMessages.length - 1];
          saveReceivedAnonData(orphan.chat_room_id, { ...latest.anonData });
        }
      }
    };

    const init = async () => {
      // Instant: render list from localStorage
      const validConnections = await loadConnectionsFromStorage('mount');
      // Async: enrich from Supabase (progressive)
      enrichConnections(validConnections);
      // Background: recover orphaned connections — if found, reload
      recoverMissedConnections()
        .then(async () => {
          const updated = await loadConnectionsFromStorage('post-recovery');
          enrichConnections(updated);
        })
        .catch(console.error);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenParams]);

  // Rebuild group filter pills when groups hook finishes loading
  useEffect(() => {
    if (groupsLoading || connections.length === 0) return;
    const groupCounts = {};
    connections.forEach((conn) => {
      const connGroups = getGroupsForConnection(conn.chatRoomId);
      connGroups.forEach((g) => {
        groupCounts[g.name] = (groupCounts[g.name] || 0) + 1;
      });
    });
    const groupsArray = Object.entries(groupCounts)
      .map(([name, count]) => ({ key: name.toLowerCase(), label: name, count }))
      .sort((a, b) => b.count - a.count);
    setAvailableGroups(groupsArray);
  }, [groupsLoading, connections, getGroupsForConnection]);

  // Handle clicking on profile (image + name) - goes to detail page
  const handleProfileClick = (chatRoomId, isManualContact = false) => {
    console.log(
      '[PROFILE_CONNECTIONS_LIST] Profile clicked, chatRoomId:',
      chatRoomId,
      'isManual:',
      isManualContact
    );

    // Update session context with the clicked connection's chatRoomId
    updateSessionChatRoomId(chatRoomId);

    // Navigate to appropriate detail page
    if (isManualContact) {
      navigateTo('manual-add-detail', {
        chatRoomId,
        source: 'connection-list',
      });
    } else {
      navigateTo('profile-connection-detail', {
        source: 'connection-list',
      });
    }
  };

  // Handle deleting a connection. Per-side (CD #15): deleteConnection marks our
  // own side via leave_connection and removes only our rows — the other person
  // keeps everything and is not told.
  const handleDeleteConnection = async (chatRoomId) => {
    console.log('[PROFILE_CONNECTIONS_LIST] Deleting connection:', chatRoomId);

    const deleted = await deleteConnection(chatRoomId);

    if (deleted) {
      // Track WHERE the delete happened (growth.md §47) — People-list swipe.
      logConnectionDeleted('people', chatRoomId);
      // Remove from displayed connections
      const updatedConnections = connections.filter((conn) => conn.chatRoomId !== chatRoomId);
      setConnections(updatedConnections);
      console.log('[PROFILE_CONNECTIONS_LIST] Connection deleted successfully');
    } else {
      console.error('[PROFILE_CONNECTIONS_LIST] Failed to delete connection');
    }
  };

  // Handle manual add button click - opens QuickAddOverlay
  // Nothing is persisted here — all saves happen in handleQuickAddSave after user confirms
  const handleManualAddClick = async () => {
    console.log('[PROFILE_CONNECTIONS_LIST] Manual add button clicked');

    // Generate unique chatRoomId for manual contact
    const timestamp = Date.now();
    const uniqueId = nanoid(6);
    const manualChatRoomId = `manual_${timestamp}_${uniqueId}`;

    console.log('[PROFILE_CONNECTIONS_LIST] Generated manual chatRoomId:', manualChatRoomId);

    // Set overlay state immediately so it opens fast
    setQuickAddChatRoomId(manualChatRoomId);
    setQuickAddMetadata(null);
    setShowQuickAdd(true);

    // Capture current location (async, don't block overlay)
    // Stored in React state only — persisted later in handleQuickAddSave if available
    try {
      const locationData = await getLocationWithVenue();
      if (locationData) {
        setQuickAddMetadata({
          location: locationData.location || locationData.venue || locationData.neighborhood,
          locationData: locationData,
          meetingDate: new Date().toISOString(),
          meetingUtcOffset: currentUtcOffsetMinutes(),
          meetingKey: mintMeetingKey(),
          timestamp: new Date().toISOString(),
          createdAt: new Date().toISOString(),
        });
        console.log('[PROFILE_CONNECTIONS_LIST] Location captured (in state):', locationData);
      }
    } catch (error) {
      console.log('[PROFILE_CONNECTIONS_LIST] Could not capture location:', error);
    }
  };

  // Handle overlay Save — persists name, note, metadata, then navigates to detail page
  const handleQuickAddSave = async ({ name, note }) => {
    const chatRoomId = quickAddChatRoomId;

    // Save manual contact data
    await updateManualContactData(chatRoomId, {
      name,
      isManualContact: true,
    });

    // Save note if provided
    if (note) {
      await updateNotes(chatRoomId, note, true);
    }

    // Always write metadata — ensures getConnections finds this contact via the
    // metadata key scan. Use captured location if available, bare timestamps otherwise.
    await updateConnectionMetadata(
      chatRoomId,
      quickAddMetadata || {
        createdAt: new Date().toISOString(),
        timestamp: new Date().toISOString(),
      }
    );

    // Write to connections table + connection_views (fire-and-forget)
    const profileUserId = localStorage.getItem('profile_user_id');
    if (profileUserId) {
      confirmHostConnection(chatRoomId, profileUserId, 'manual').then(() => {
        upsertConnectionView(chatRoomId, profileUserId, {
          displayName: name,
          notes: note || null,
          metadata: quickAddMetadata,
        });
      });
    }

    // Analytics: log manual add + location backfill
    logManualAdd(chatRoomId, name, !!note, 'Other');
    if (quickAddMetadata?.locationData) {
      logConnectionLocation(chatRoomId, quickAddMetadata.locationData, 'quick_add');
    }

    // Navigate to detail page for adding contacts, tags, etc.
    updateSessionChatRoomId(chatRoomId);
    navigateTo('manual-add-detail', { chatRoomId, source: 'quick-add' });

    // Clean up overlay state
    setShowQuickAdd(false);
    setQuickAddChatRoomId(null);
    setQuickAddMetadata(null);
  };

  // Handle overlay dismiss (backdrop tap) — nothing saved, no connection created
  const handleQuickAddClose = () => {
    setShowQuickAdd(false);
    setQuickAddChatRoomId(null);
    setQuickAddMetadata(null);
  };

  const currentSenderId = getSenderId();

  // Render one connection card. Shared by the normal list and the Nearby tab's
  // grouped/flat lists so the card prop wiring lives in exactly one place.
  // trailingOverlay (TEXT camera) rides inside the swipe layer so it moves
  // with the card and never covers the swipe-revealed delete button.
  const renderCard = (connection, trailingOverlay = null) => {
    const latestMessage = connection.latestMessage || null;
    const { recentMessage, isMessageFromUser } = formatMessagePreview(
      latestMessage,
      currentSenderId
    );

    // Display timestamp — always met time on People list
    const displayTime =
      connection.connectionMetadata?.timestamp ||
      connection.connectionMetadata?.meetingDate ||
      null;

    // Groups for this connection (with pill suppression)
    const connGroups = getDisplayGroups(connection.chatRoomId);

    return (
      <SwipeableConnectionCard
        key={connection.chatRoomId}
        connection={connection}
        onDelete={() => handleDeleteConnection(connection.chatRoomId)}
        onProfileClick={() => handleProfileClick(connection.chatRoomId, connection.isManualContact)}
        onCardClick={() => handleProfileClick(connection.chatRoomId, connection.isManualContact)}
        // Chat re-entry, matching the Anon People page. Not for manual
        // contacts (no room to open), and dormant under sans-chat variants —
        // the same gate AnonPreviewCard uses.
        onChatClick={
          connection.isManualContact || isSansChatVariant()
            ? null
            : () => {
                updateSessionChatRoomId(connection.chatRoomId);
                navigateTo('profile-connection-page');
              }
        }
        getFormattedLocation={formatLocation}
        recentMessage={recentMessage}
        isMessageFromUser={isMessageFromUser}
        isManualContact={connection.isManualContact}
        hasUnreadMessage={hasUnreadMessages(
          latestMessage ? [latestMessage] : [],
          connection.chatRoomId
        )}
        displayTimestamp={formatRelativeTime(displayTime)}
        isRoyaleUser={isRoyaleUser(connection.anonData)}
        variant="connection"
        // Resolved, not raw: the drain-into-nickname migration only runs when
        // a detail page mounts, so a legacy note-only connection showed a blank
        // subtitle here until someone happened to open it. Reading through the
        // resolver shows the right text immediately and is a no-op afterwards.
        nickname={resolveConnectionNote(connection).text || null}
        groups={connGroups}
        locationDisplay={displayLocations[connection.chatRoomId] || null}
        hometown={getSharedHometown(connection.anonData)}
        // Sans-chat: latest shared photo as the card thumbnail (mock 7a) —
        // the visible receipt that a host photo-share posted
        photoThumbUrl={isSansChatVariant() ? latestSharedImageUrl([latestMessage]) : null}
        trailingOverlay={trailingOverlay}
      />
    );
  };

  // Host-flow render gates (Step 7, REVISED at device pass 1 — user ruling
  // 2026-08-08): the waiting card converts to a real card only when a NAME
  // arrives; a formed-but-nameless room keeps the spinner while the moment is
  // live and simply never renders a card ("no more Add-name cards"). Nameless
  // connections are filtered from the list below for the same reason.
  const hostName = hostFlowData?.anonData?.name || '';
  // TEXT (plan 2): the full-page confirm owns the screen during the venue
  // stage; the card shows from the camera stage on, name NOT required (the
  // "Name" placeholder — lurker cards accepted, build-for-me ruling).
  const showTextVenueConfirm = Boolean(textVariant && hostFlowRoom && hostCardStage === 'venue');
  const showHostCard = textVariant
    ? Boolean(hostFlowRoom && hostCardStage && hostCardStage !== 'venue')
    : Boolean(hostFlowRoom && hostCardStage && hostName);
  // Waiting card ONLY in the scan→name window (device pass 2 ruling: never
  // pre-scan — generating a QR that nobody scanned must not leave a
  // "Waiting..." card on People). justFormed params exist only after a real
  // scan formed the room; until the share-back name lands, the spinner holds
  // the slot, then the named card takes over (B2/B5). CARD-only: TEXT's
  // placeholder card is immediate, so there is nothing to wait for.
  const showWaitingCard = !textVariant && Boolean(hostFlowRoom && hostCardStage && !hostName);
  // Nameless filter is CARD's no-name-no-card rule — TEXT deliberately
  // reverses it (the list card renders its own "Add name" placeholder).
  const visibleConnections =
    isSansChatVariant() && !textVariant
      ? filteredConnections.filter(
          (c) => c.isManualContact || getConnectionDisplayName(c) || c.nickname
        )
      : filteredConnections;
  // The host-flow card's place line, on the existing rails (device pass 2:
  // this line hand-rolled "Met at" and bypassed both the venue gate and the
  // at/in convention): formatMetSentence gives "Met at REI" / "Met in
  // Norwalk". The VENUE STAGE force-opens the gate — showing the unconfirmed
  // guess there IS the confirm prompt; every other stage reads gated, so a
  // skipped venue falls to the neighborhood. Time rides confirmation (user
  // ruling): it joins only once venueConfirmed.
  const hostVenueLine = (() => {
    if (!hostMetadata) return null;
    // Beyond the confirm stage, the GATED clone is the sentence context too —
    // when a place is venue AND neighborhood under one name (Van Cortlandt
    // Park, device pass 2), the stripped venue can't claim the preposition
    // match, so the area correctly reads "Met in", not "Met at".
    const meta =
      hostCardStage === 'venue'
        ? { ...hostMetadata, venueConfirmed: true }
        : gateVenueMetadata(hostMetadata);
    const place = formatLocationPillPrimary(meta);
    if (!place) return null;
    const sentence = formatMetSentence(place, meta);
    if (!hostMetadata.venueConfirmed) return sentence;
    const time = formatRelativeTime(hostMetadata.meetingDate || hostMetadata.timestamp);
    return time ? `${sentence}, ${time}` : sentence;
  })();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        overflow: 'hidden',
        backgroundColor: COLORS.background,
      }}
    >
      {/* Fixed header — title row only. Simple tier: SP avatar replaces the
          toggle + "+" (mock 7a — the profile-access point, no tab bar). */}
      <ProfileConnectionsHeader
        devTools={devToolsOn ? <DevToolsPills onLocClick={() => setShowLocPanel(true)} /> : null}
        onManualAddClick={handleManualAddClick}
        isLocationSharing={isLocationSharing}
        onLocationToggle={handleLocationToggle}
        spAvatar={isSimpleTier ? { photoUrl: spProfile?.photoUrl, name: spProfile?.name } : null}
        onSpAvatarClick={() => navigateTo('profile-settings', { source: 'sp-header' })}
      />

      {/* Search bar + filter chips — fixed below header, slides with scroll
          direction. Hidden at Simple tier (mock 7a: card list only). */}
      {!isSimpleTier && (
        <div
          style={{
            position: 'fixed',
            top: showSearchBar
              ? 'calc(env(safe-area-inset-top) + 72px)'
              : 'calc(env(safe-area-inset-top) + 72px - 100px)',
            left: 0,
            right: 0,
            zIndex: 99,
            backgroundColor: '#FFFFFF',
            padding: '0 16px 12px 16px',
            transition: 'top 0.25s ease',
          }}
        >
          <div
            style={{
              maxWidth: '600px',
              margin: '0 auto',
              boxSizing: 'border-box',
            }}
          >
            {/* Search bar */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 14px',
                backgroundColor: '#FFFFFF',
                borderRadius: '20px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                marginBottom: '12px',
              }}
            >
              <MagnifyingGlass
                size={18}
                color="#C2C2C8"
                weight="regular"
                style={{ flexShrink: 0 }}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search"
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                  fontSize: '16px',
                  fontWeight: '300',
                  color: '#000',
                  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
                }}
              />
            </div>

            {/* Filter chips — horizontal scroll. All + Nearby are hardcoded;
              group chips are derived from availableGroups. */}
            {connections.length > 0 && (
              <div
                className="hide-scrollbar"
                style={{
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  margin: '0 -16px',
                  padding: '0 16px',
                }}
              >
                <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div
                    onClick={() => {
                      setSelectedCategory(null);
                      setNearbyActive(false);
                    }}
                    style={{
                      padding: '6px 14px',
                      backgroundColor: !selectedCategory && !nearbyActive ? '#E8E8EA' : '#F7F7F9',
                      color: !selectedCategory && !nearbyActive ? '#6A6F7A' : '#ABB0BC',
                      borderRadius: '16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                    }}
                  >
                    All
                  </div>
                  {/* Nearby — hardcoded mode (not group-derived) */}
                  <div
                    onClick={() => {
                      setNearbyActive(true);
                      setSelectedCategory(null);
                      logEvent('nearby_viewed');
                    }}
                    style={{
                      padding: '6px 14px',
                      backgroundColor: nearbyActive ? '#E8E8EA' : '#F7F7F9',
                      color: nearbyActive ? '#6A6F7A' : '#ABB0BC',
                      borderRadius: '16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                    }}
                  >
                    Nearby
                  </div>
                  {/* Group filter chips */}
                  {availableGroups.map((tag) => {
                    const isSelected = selectedCategory === tag.key && !nearbyActive;
                    return (
                      <div
                        key={tag.key}
                        onClick={() => {
                          setSelectedCategory(selectedCategory === tag.key ? null : tag.key);
                          setNearbyActive(false);
                        }}
                        style={{
                          padding: '6px 14px',
                          backgroundColor: isSelected ? '#E8E8EA' : '#F7F7F9',
                          color: isSelected ? '#6A6F7A' : '#ABB0BC',
                          borderRadius: '16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                        }}
                      >
                        {tag.label}
                      </div>
                    );
                  })}
                  {/* DEV-only: Nearby test-data toggle (hidden in production) */}
                  {process.env.REACT_APP_BRANCH !== 'main' && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginLeft: '4px',
                        paddingLeft: '12px',
                        borderLeft: '1px solid #E8E8EA',
                      }}
                    >
                      {['light', 'full', 'live'].map((mode) => (
                        <span
                          key={mode}
                          onClick={() => toggleTestData(mode)}
                          style={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: testMode === mode ? '#007AFF' : '#C2C2C8',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            textTransform: 'capitalize',
                          }}
                        >
                          {mode}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Scrollable Content */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          // Simple tier has no search/chips block — header only (mock 7a)
          // SP matches the Anon People page exactly (72px) — same header shape,
          // so the first card must start at the same height (user 2026-08-16)
          paddingTop: isSimpleTier ? '72px' : '168px', // Full: header + search/chips (~84px)
          paddingBottom: isSimpleTier ? '260px' : '80px', // SP: Connect pill + the QR reminder
        }}
      >
        {isLoading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
        ) : nearbyActive ? (
          <NearbyList
            groups={nearbyGroups}
            loading={nearbyLoading}
            hasAnchor={nearbyHasAnchor}
            isLocationSharing={isLocationSharing}
            searchQuery={searchQuery}
            searchResults={filteredConnections}
            renderCard={renderCard}
            onShareLocation={handleLocationToggle}
            onSetHometown={() => navigateTo('profile-settings')}
          />
        ) : visibleConnections.length === 0 && !showWaitingCard && !showHostCard ? (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              paddingTop: '30vh',
              minHeight: 'calc(100vh - 280px)',
            }}
          >
            {/* Different empty state depending on context */}
            {connections.length === 0 ? (
              <div style={{ fontSize: '40px', fontWeight: '600', color: '#000000' }}>Welcome</div>
            ) : (
              <>
                <div style={{ fontSize: '16px', color: '#666666' }}>No matches</div>
                <div
                  onClick={() => {
                    setSelectedCategory(null);
                    setSearchQuery('');
                  }}
                  style={{
                    fontSize: '14px',
                    color: '#007AFF',
                    cursor: 'pointer',
                    marginTop: '4px',
                  }}
                >
                  Clear filters
                </div>
              </>
            )}
          </div>
        ) : (
          <div
            style={{
              padding: '0 16px 20px 16px',
              width: '100%',
              maxWidth: '600px',
              margin: '0 auto',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {/* Host card flow (Step 7) — waiting card, then the just-formed
                card with inline prompts; met-desc sort pins it on top */}
            {/* ✕ = abandoned scan (peer never entered a name): settle via the
                'done' sentinel — the DB connection is untouched, so a name
                arriving later still renders the normal card (name-gated list) */}
            {showWaitingCard && <WaitingCard onDismiss={() => advanceHostStage(null)} />}
            {showHostCard && (
              <JustFormedCard
                name={hostName}
                namePlaceholder={textVariant ? 'Name' : undefined}
                cameraIconOnly={textVariant}
                onCardClick={
                  textVariant ? () => handleProfileClick(hostFlowRoom, false) : undefined
                }
                venueLine={hostVenueLine}
                stage={hostCardStage}
                onVenueEdit={() => setShowHostVenueOverlay(true)}
                onVenueConfirm={handleHostVenueConfirm}
                onVenueSkip={() => advanceHostStage('photo')}
                onTakePhoto={(file) => handleCardPhoto(hostFlowRoom, file)}
                onPhotoTap={() => logMomentEntryTapped(hostFlowRoom, 'host_flow')}
                onPhotoSkip={() => advanceHostStage(null)}
              />
            )}
            {/* TEXT: one-shot send prompt under the just-formed card once the
                number has arrived (never behind the venue confirm) */}
            {showHostCard && shouldShowSendPrompt(hostFlowRoom, hostFlowData?.anonData) && (
              <SendMessagePrompt
                name={firstNameOf(hostName)}
                verb={pickDmChannel(hostFlowData?.anonData)?.type === 'phone' ? 'Text' : 'Message'}
                onSend={() => setSendOverlayRoom(hostFlowRoom)}
                onDismiss={() => retireSendPrompt(hostFlowRoom)}
              />
            )}
            {visibleConnections
              .filter((c) => !(showHostCard && c.chatRoomId === hostFlowRoom))
              .map((connection) => {
                const showPrompt = shouldShowSendPrompt(connection.chatRoomId, connection.anonData);
                const showCamera = shouldShowCardCamera(
                  connection.chatRoomId,
                  connection.connectionMetadata
                );
                // Camera overlays the card's right edge (mock placement),
                // riding inside the swipe layer via renderCard
                const card = renderCard(
                  connection,
                  showCamera ? (
                    <CardCameraButton
                      onFile={(file) => handleCardPhoto(connection.chatRoomId, file)}
                    />
                  ) : null
                );
                if (!showPrompt) return card;
                return (
                  <div
                    key={connection.chatRoomId}
                    style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                  >
                    {card}
                    <SendMessagePrompt
                      name={firstNameOf(getConnectionDisplayName(connection) || '')}
                      verb={
                        pickDmChannel(connection.anonData)?.type === 'phone' ? 'Text' : 'Message'
                      }
                      onSend={() => setSendOverlayRoom(connection.chatRoomId)}
                      onDismiss={() => retireSendPrompt(connection.chatRoomId)}
                    />
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* TEXT venue confirm — auto-raised bottom sheet (device pass 1 ruling:
          full page too much, inline row too tiny; the sheet orients without
          trapping). Confirm/Skip ride the SAME handlers as CARD's inline row;
          EDIT opens the existing LocationOverlay picker (zIndex 2100, above
          the sheet — a pick advances the stage and the sheet leaves).
          Backdrop tap ≡ Skip. */}
      <AnimatePresence>
        {showTextVenueConfirm && (
          <HostTextVenueConfirm
            connectionMetadata={hostMetadata}
            onConfirm={handleHostVenueConfirm}
            onEdit={() => setShowHostVenueOverlay(true)}
            onSkip={() => advanceHostStage('photo')}
          />
        )}
      </AnimatePresence>

      {/* TEXT send overlay — editable draft, link checkbox default OFF;
          backdrop close keeps the prompt (only the prompt's X or a real send
          retires the one-shot) */}
      {sendOverlayRoom && sendOverlayData && (
        <SendMessageOverlay
          anonName={sendOverlayData.anonData?.name || ''}
          connectionMetadata={sendOverlayData.connectionMetadata || null}
          channelType={sendOverlayChannel?.type || 'phone'}
          onSend={handleSendOverlaySend}
          onClose={() => setSendOverlayRoom(null)}
        />
      )}

      {/* Host venue picker (✏️ on the just-formed card) — same wiring as the
          dormant chat page's LocationOverlay */}
      {showHostVenueOverlay && hostMetadata && (
        <LocationOverlay
          nearbyVenues={hostMetadata?.locationData?.venueInfo?.nearbyVenues || []}
          currentVenue={hostMetadata?.locationData?.venueInfo?.venue || null}
          address={hostMetadata?.locationData?.venueInfo?.address || {}}
          coords={hostMetadata?.locationData?.coords || {}}
          showLocationContext={false}
          onVenueChange={handleHostVenuePick}
          onClose={() => setShowHostVenueOverlay(false)}
        />
      )}

      {/* Quick Add Overlay */}
      <AnimatePresence>
        {showQuickAdd && quickAddChatRoomId && (
          <QuickAddOverlay onSave={handleQuickAddSave} onClose={handleQuickAddClose} />
        )}
      </AnimatePresence>

      {/* Share-location bottom sheet */}
      <AnimatePresence>
        {showShareSheet && (
          <ShareLocationSheet
            isSharing={isLocationSharing}
            initialLocation={ownLocation}
            onConfirm={handleShareSheetConfirm}
            onClose={() => {
              setShowShareSheet(false);
              setShareError(null);
            }}
            error={shareError}
            submitting={shareSubmitting}
          />
        )}
      </AnimatePresence>

      {/* Location Permission Overlay (pre-prompt) — slide-up bottom sheet */}
      <AnimatePresence>
        {showLocationPrompt && (
          <LocationPrompt context="sharing" onComplete={handleLocationPromptComplete} />
        )}
      </AnimatePresence>

      {/* Simple tier (7b): the Connect-tap location ask — soft pre-prompt,
          once; the QR opens regardless of the answer */}
      <AnimatePresence>
        {isSimpleTier && spLocationPrompt.showLocationPrompt && (
          <LocationPrompt
            context="connecting"
            onComplete={(result) => {
              spLocationPrompt.handlePromptComplete(result);
              showQR();
            }}
          />
        )}
      </AnimatePresence>

      {/* Simple tier: "⚡ Connect" pill + Get-app banner (mocks 7a/7d).
          Connect opens the SP's own QR (goal vs mechanism, same button — D4);
          the banner retires once the waitlist join lands (anon_9). */}
      {isSimpleTier && (
        <div
          style={{
            position: 'fixed',
            // Same offsets, gap and inset as the Anon People page's group —
            // the two pages are the same person one tap apart (user 2026-08-16)
            bottom: 'calc(24px + env(safe-area-inset-bottom))',
            left: 0,
            right: 0,
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '22px',
            padding: '0 20px',
            pointerEvents: 'none',
          }}
        >
          {/* The two-line reminder — no "try a QR", they have one. It stays
              until their QR actually produces a connection (user idea
              2026-08-16): retiring on COMPLETION rather than on exposure, and
              the window it covers is exactly the step-2 → step-3 gap the
              milestone measures. */}
          {!hasSimpleHostedExchange() && (
            <div style={{ width: '100%', maxWidth: '560px' }}>
              <PeopleQrPitch showQrLine={false} />
            </div>
          )}
          {/* Above the card in z-order so the card rises from behind it */}
          <div style={{ pointerEvents: 'auto', position: 'relative', zIndex: 2 }}>
            <ButtonRounded
              text={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <Lightning size={16} weight="fill" />
                  Connect
                </span>
              }
              onClick={handleSpConnectPress}
              variant="primary"
              size="auto"
              style={{ minWidth: '140px' }}
            />
          </div>
        </div>
      )}

      {/* Notification permission pre-prompt — first People-list visit, slide-up bottom sheet */}
      <AnimatePresence>
        {showNotificationPrompt && (
          <NotificationPrompt
            onComplete={() => {
              markNotificationPromptSeen();
              setShowNotificationPrompt(false);
            }}
          />
        )}
      </AnimatePresence>

      {/* Browser permission help — shown on a code-1 denial; terminal (single dismiss) */}
      <AnimatePresence>
        {showBrowserPermissionModal && (
          <BrowserPermissionModal onClose={handleDismissPermissionModal} />
        )}
      </AnimatePresence>

      {/* The LOC panel's overlay half. Its opener is the header pill; the pill
          itself is branch-gated inside DevToolsPills, so on main this never
          opens. Kept at page level rather than in the header because it is a
          full-width fixed overlay, not header chrome. */}
      {devToolsOn && isBranchDeploy() && (
        <LocationDebugPanel open={showLocPanel} onClose={() => setShowLocPanel(false)} />
      )}
    </div>
  );
};

export default ProfileConnectionsListPage;
